
import React from 'react';
import ModernPolicyLayout from '@/components/legal/ModernPolicyLayout';

const CookiePolicy = () => {
  const tableOfContents = [
    { id: 'what-are-cookies', title: 'What Are Cookies', level: 1 },
    { id: 'types-of-cookies', title: 'Types of Cookies We Use', level: 1 },
    { id: 'essential-cookies', title: 'Essential Cookies', level: 2 },
    { id: 'analytics-cookies', title: 'Analytics Cookies', level: 2 },
    { id: 'functional-cookies', title: 'Functional Cookies', level: 2 },
    { id: 'third-party-cookies', title: 'Third-Party Cookies', level: 1 },
    { id: 'cookie-management', title: 'How to Control Cookies', level: 1 },
    { id: 'browser-settings', title: 'Browser Settings', level: 2 },
    { id: 'opt-out', title: 'Opt-Out Options', level: 2 },
    { id: 'cookie-consent', title: 'Cookie Consent', level: 1 },
    { id: 'updates', title: 'Policy Updates', level: 1 },
    { id: 'contact', title: 'Contact Us', level: 1 }
  ];

  return (
    <ModernPolicyLayout 
      title="Cookie Policy" 
      estimatedReadTime="6 min read"
      lastUpdated="Last updated: October 10, 2025"
      tableOfContents={tableOfContents}
    >
      <div className="space-y-8">
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
          <p>
            This Cookie Policy explains how EquiForge™ Homebuyer Optimizer ("we," "us," or "our") uses 
            cookies and similar tracking technologies when you access or use our application. By using 
            our application, you consent to our use of cookies in accordance with this policy.
          </p>
        </div>

        <section id="what-are-cookies">
          <h2>1. What Are Cookies</h2>
          <p>
            Cookies are small text files that are placed on your device (computer, smartphone, or tablet) 
            when you visit websites or use applications. They are widely used to make applications work 
            more efficiently and provide information to the application owners.
          </p>
          <p>
            Cookies serve several purposes:
          </p>
          <ul>
            <li><strong>Session Management:</strong> Remember your login status and preferences</li>
            <li><strong>Personalization:</strong> Store your settings and customizations</li>
            <li><strong>Analytics:</strong> Understand how users interact with the application</li>
            <li><strong>Security:</strong> Protect against fraudulent activity and enhance security</li>
          </ul>
        </section>

        <section id="types-of-cookies">
          <h2>2. Types of Cookies We Use</h2>
          <p>
            EquiForge™ uses different types of cookies for various purposes. Below is a detailed 
            breakdown of each category:
          </p>

          <div id="essential-cookies" className="mt-6">
            <h3>2.1 Essential Cookies (Strictly Necessary)</h3>
            <p>
              These cookies are required for the application to function properly. They enable core 
              functionality such as security, authentication, and accessibility features. The application 
              cannot function properly without these cookies.
            </p>
            <div className="bg-card border rounded-lg p-4 mt-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Cookie Name</th>
                    <th className="text-left py-2">Purpose</th>
                    <th className="text-left py-2">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 font-mono text-xs">sb-auth-token</td>
                    <td className="py-2">Authentication and session management</td>
                    <td className="py-2">7 days</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-mono text-xs">csrf-token</td>
                    <td className="py-2">Security protection against CSRF attacks</td>
                    <td className="py-2">Session</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 font-mono text-xs">user-preferences</td>
                    <td className="py-2">Store user interface preferences</td>
                    <td className="py-2">1 year</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-mono text-xs">cookie-consent</td>
                    <td className="py-2">Remember your cookie preferences</td>
                    <td className="py-2">1 year</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              <strong>Note:</strong> Essential cookies cannot be disabled without severely affecting 
              your ability to use the application.
            </p>
          </div>

          <div id="analytics-cookies" className="mt-6">
            <h3>2.2 Analytics and Performance Cookies</h3>
            <p>
              These cookies help us understand how visitors interact with our application by collecting 
              and reporting anonymous information. This helps us improve the application's performance 
              and user experience.
            </p>
            <div className="bg-card border rounded-lg p-4 mt-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Service</th>
                    <th className="text-left py-2">Purpose</th>
                    <th className="text-left py-2">Data Collected</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2">Google Analytics</td>
                    <td className="py-2">Usage analytics and user behavior tracking</td>
                    <td className="py-2">Page views, session duration, device info</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2">PostHog</td>
                    <td className="py-2">Product analytics and feature usage</td>
                    <td className="py-2">User interactions, feature usage, funnels</td>
                  </tr>
                  <tr>
                    <td className="py-2">Sentry</td>
                    <td className="py-2">Error tracking and performance monitoring</td>
                    <td className="py-2">Error logs, performance metrics, stack traces</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4">
              <strong>Analytics Data:</strong> All analytics data is anonymized and aggregated. We do 
              not use this data to identify individual users.
            </p>
          </div>

          <div id="functional-cookies" className="mt-6">
            <h3>2.3 Functional Cookies</h3>
            <p>
              These cookies enable enhanced functionality and personalization, such as remembering your 
              calculator inputs, display preferences, and recently viewed properties.
            </p>
            <ul>
              <li>Saved calculator values and mortgage scenarios</li>
              <li>User interface customization (theme, layout)</li>
              <li>Language and region preferences</li>
              <li>Recently viewed properties and searches</li>
              <li>Dashboard widget configurations</li>
            </ul>
            <p className="mt-4 text-sm text-muted-foreground">
              You can control functional cookies through your account settings or browser preferences.
            </p>
          </div>
        </section>

        <section id="third-party-cookies">
          <h2>3. Third-Party Cookies</h2>
          <p>
            In addition to our own cookies, we use third-party services that may set cookies on your 
            device. These services are essential for the application's functionality:
          </p>

          <div className="space-y-4 mt-4">
            <div className="bg-card border rounded-lg p-4">
              <h4 className="font-semibold mb-2">Authentication Services</h4>
              <ul className="text-sm space-y-1">
                <li><strong>Supabase Auth:</strong> User authentication and session management</li>
              </ul>
            </div>

            <div className="bg-card border rounded-lg p-4">
              <h4 className="font-semibold mb-2">Payment Processing</h4>
              <ul className="text-sm space-y-1">
                <li><strong>Stripe:</strong> Payment processing and subscription management</li>
                <li>Cookies: Fraud detection, secure transactions, PCI compliance</li>
              </ul>
            </div>

            <div className="bg-card border rounded-lg p-4">
              <h4 className="font-semibold mb-2">Content Delivery</h4>
              <ul className="text-sm space-y-1">
                <li><strong>Cloudflare:</strong> Performance optimization and security</li>
                <li><strong>Supabase CDN:</strong> Static asset delivery</li>
              </ul>
            </div>

            <div className="bg-card border rounded-lg p-4">
              <h4 className="font-semibold mb-2">Analytics and Monitoring</h4>
              <ul className="text-sm space-y-1">
                <li><strong>Google Analytics:</strong> Anonymous usage analytics</li>
                <li><strong>PostHog:</strong> Product analytics and session recording</li>
                <li><strong>Sentry:</strong> Error tracking and performance monitoring</li>
              </ul>
            </div>
          </div>

          <p className="mt-4">
            <strong>Third-Party Privacy Policies:</strong> Each third-party service has its own privacy 
            policy governing their use of cookies. We recommend reviewing these policies:
          </p>
          <ul className="text-sm mt-2">
            <li><a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Supabase Privacy Policy</a></li>
            <li><a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Stripe Privacy Policy</a></li>
            <li><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Privacy Policy</a></li>
          </ul>
        </section>

        <section id="cookie-management">
          <h2>4. How to Control Cookies</h2>
          <p>
            You have several options to manage and control cookies used by EquiForge™:
          </p>

          <div id="browser-settings" className="mt-6">
            <h3>4.1 Browser Settings</h3>
            <p>
              Most web browsers allow you to control cookies through their settings. You can typically:
            </p>
            <ul>
              <li>View and delete existing cookies</li>
              <li>Block third-party cookies</li>
              <li>Block all cookies (not recommended for EquiForge™)</li>
              <li>Clear cookies when you close the browser</li>
            </ul>

            <div className="bg-card border rounded-lg p-4 mt-4">
              <h4 className="font-semibold mb-3">Browser-Specific Instructions:</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data
                </li>
                <li>
                  <strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data
                </li>
                <li>
                  <strong>Safari:</strong> Preferences → Privacy → Manage Website Data
                </li>
                <li>
                  <strong>Edge:</strong> Settings → Cookies and site permissions → Manage and delete cookies
                </li>
              </ul>
            </div>

            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 rounded-lg p-4 mt-4">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Warning:</strong> Blocking all cookies will prevent you from using essential 
                features of EquiForge™, including authentication, saved calculations, and personalized 
                settings.
              </p>
            </div>
          </div>

          <div id="opt-out" className="mt-6">
            <h3>4.2 Opt-Out Options</h3>
            <p>
              You can opt out of specific types of cookies:
            </p>

            <div className="space-y-3 mt-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                  <span className="text-xs font-semibold">1</span>
                </div>
                <div>
                  <p className="font-semibold">Analytics Cookies</p>
                  <p className="text-sm text-muted-foreground">
                    Opt out via our Cookie Preferences center or use browser privacy settings
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                  <span className="text-xs font-semibold">2</span>
                </div>
                <div>
                  <p className="font-semibold">Google Analytics</p>
                  <p className="text-sm text-muted-foreground">
                    Install the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Analytics Opt-out Browser Add-on</a>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                  <span className="text-xs font-semibold">3</span>
                </div>
                <div>
                  <p className="font-semibold">Advertising Cookies</p>
                  <p className="text-sm text-muted-foreground">
                    Currently, EquiForge™ does not use advertising cookies. This may change in the future.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="cookie-consent">
          <h2>5. Cookie Consent Management</h2>
          <p>
            When you first visit EquiForge™, we display a cookie consent banner that allows you to:
          </p>
          <ul>
            <li>Accept all cookies</li>
            <li>Reject non-essential cookies</li>
            <li>Customize your cookie preferences by category</li>
            <li>Learn more about each cookie category</li>
          </ul>

          <div className="bg-card border rounded-lg p-4 mt-4">
            <p className="font-semibold mb-2">Your Consent Choices:</p>
            <ul className="text-sm space-y-1">
              <li>✅ <strong>Essential cookies:</strong> Always active (required for functionality)</li>
              <li>⚙️ <strong>Functional cookies:</strong> Optional (enhance experience)</li>
              <li>📊 <strong>Analytics cookies:</strong> Optional (help us improve)</li>
              <li>🎯 <strong>Marketing cookies:</strong> Not currently used</li>
            </ul>
          </div>

          <p className="mt-4">
            You can change your cookie preferences at any time through:
          </p>
          <ul>
            <li>The "Cookie Settings" link in the application footer</li>
            <li>Your account settings under "Privacy Preferences"</li>
            <li>Clicking "Manage Cookie Preferences" in this policy</li>
          </ul>

          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-4 mt-4">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>GDPR & CCPA Compliance:</strong> If you are located in the EU, UK, or California, 
              you have additional rights regarding cookies and data collection. See our Privacy Policy 
              for more information about your data rights.
            </p>
          </div>
        </section>

        <section id="updates">
          <h2>6. Updates to This Cookie Policy</h2>
          <p>
            We may update this Cookie Policy from time to time to reflect changes in our practices, 
            technologies, legal requirements, or for other operational reasons.
          </p>
          <p>
            <strong>Notification of Changes:</strong> Material changes to this policy will be 
            communicated through:
          </p>
          <ul>
            <li>Email notification to registered users</li>
            <li>In-app notification banner</li>
            <li>Updated "Last Updated" date at the top of this policy</li>
          </ul>
          <p>
            We encourage you to review this Cookie Policy periodically to stay informed about our use 
            of cookies.
          </p>
        </section>

        <section id="contact">
          <h2>7. Contact Us</h2>
          <p>
            If you have questions or concerns about our use of cookies, please contact us:
          </p>
          <div className="bg-card border rounded-lg p-4 mt-4">
            <p><strong>Email:</strong> privacy@equiforge.com</p>
            <p><strong>Support:</strong> support@equiforge.com</p>
            <p><strong>Legal Department:</strong> legal@equiforge.com</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Please allow up to 3 business days for a response to cookie-related inquiries.
            </p>
          </div>

          <p className="mt-6 text-sm text-muted-foreground">
            <strong>Effective Date:</strong> This Cookie Policy is effective as of October 10, 2025.
          </p>
        </section>
      </div>
    </ModernPolicyLayout>
  );
};

export default CookiePolicy;
